import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditCalendarOutlinedIcon from "@mui/icons-material/EditCalendarOutlined";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { findByTurmaAnoLetivo } from "../Professor/api-integral-professor";
import { findByIntegralProfessorCodigo, remove, save } from "./api-integral-presenca";
import { dateFormat, dayOfWeekNameByDate } from "../../../util/DateUtil";
import { errorMessage } from "../../../util/ConfirmAlertUtil";
import { success } from "../../../util/ToastUtil";
import Container from "../../UI/Container";
import Loading from "../../UI/Loading";
import IntegralProfessorTable from "../Professor/IntegralProfessorTable";
import IntegralPresencaAlunoTable from "../PresencaAluno/IntegralPresencaAlunoTable";
import IntegralPresencaForm from "./IntegralPresencaForm";
import IntegralPresencaTable from "./IntegralPresencaTable";

const EMPTY_PAGE = { content: [], size: 0, totalElements: 0, number: 0 };

const IntegralPresencaContainer = () => {
  const [listaIP, setListaIP] = useState([]);
  const [isLoadingIP, setIsLoadingIP] = useState(true);
  const [integralProfessor, setIntegralProfessor] = useState(null);
  const [listaPresenca, setListaPresenca] = useState(EMPTY_PAGE);
  const [pageNumber, setPageNumber] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [isLoadingP, setIsLoadingP] = useState(false);
  const [integralPresenca, setIntegralPresenca] = useState(null);
  const [editando, setEditando] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const codIP = integralProfessor?.codigo;

  useEffect(() => {
    findByTurmaAnoLetivo()
      .then((data) => {
        if (!data) return;
        if (data.error) {
          errorMessage(data.message || "Não foi possível carregar as turmas do integral.");
        } else if (Array.isArray(data)) {
          setListaIP(data);
        }
      })
      .finally(() => setIsLoadingIP(false));
  }, []);

  useEffect(() => {
    if (!codIP) {
      setListaPresenca(EMPTY_PAGE);
      return;
    }
    let active = true;
    setIsLoadingP(true);
    findByIntegralProfessorCodigo(codIP, pageNumber)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(data.message || "Não foi possível carregar os dias registrados.");
        } else {
          setListaPresenca({ ...EMPTY_PAGE, ...data });
        }
      })
      .finally(() => {
        if (active) setIsLoadingP(false);
      });
    return () => {
      active = false;
    };
  }, [codIP, pageNumber, reloadKey]);

  const handleSelectIP = (ip) => {
    setIntegralProfessor(ip);
    setIntegralPresenca(null);
    setPageNumber(0);
  };

  const handleSave = (persist) => {
    const eNovo = !persist.codigo;
    setIsSaving(true);
    save(persist)
      .then((data) => {
        if (!data || data.error) {
          errorMessage(data?.message || "Não foi possível salvar o dia.");
          return;
        }
        success("Salvo com sucesso.");
        setEditando(null);
        // Um dia novo abre direto a chamada.
        setIntegralPresenca(data);
        if (eNovo) setPageNumber(0);
        setReloadKey((key) => key + 1);
      })
      .finally(() => setIsSaving(false));
  };

  const handleRemove = () =>
    confirmAlert({
      title: "Alerta",
      message: `Deseja remover o registro de ${dateFormat(integralPresenca.data)}? A chamada deste dia também será removida.`,
      buttons: [
        {
          label: "Sim",
          onClick: () => {
            remove(integralPresenca.codigo).then((data) => {
              if (!data || data.error) {
                errorMessage(data?.message || "Não foi possível remover o registro.");
                return;
              }
              success("Registro removido.");
              setIntegralPresenca(null);
              if (listaPresenca.content.length === 1 && pageNumber > 0) {
                setPageNumber((p) => p - 1);
              } else {
                setReloadKey((key) => key + 1);
              }
            });
          },
        },
        { label: "Não", onClick: () => {} },
      ],
    });

  const renderHeader = () => {
    if (integralPresenca) {
      return (
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} mb={3}>
          <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
            <Tooltip title="Voltar para os dias">
              <IconButton edge="start" onClick={() => setIntegralPresenca(null)}>
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Box minWidth={0}>
              <Typography variant="h6" noWrap>
                Chamada de {dateFormat(integralPresenca.data)}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {dayOfWeekNameByDate(integralPresenca.data)} · {integralProfessor.turma?.descricao}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Alterar data">
              <IconButton onClick={() => setEditando(integralPresenca)}>
                <EditCalendarOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Remover registro">
              <IconButton color="error" onClick={handleRemove}>
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      );
    }
    return (
      <Stack direction="row" spacing={1} alignItems="center" mb={3}>
        <Tooltip title="Menu Integral">
          <IconButton edge="start" component={Link} to="/integral">
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Box>
          <Typography variant="h6">Presença Integral</Typography>
          <Typography variant="body2" color="text.secondary">
            Registre os dias e faça a chamada dos alunos do integral.
          </Typography>
        </Box>
      </Stack>
    );
  };

  const renderContent = () => {
    if (isLoadingIP) {
      return (
        <Box py={6}>
          <Loading description="Carregando turmas do integral..." />
        </Box>
      );
    }
    if (!integralProfessor) {
      return (
        <Box>
          <Typography variant="subtitle2" mb={1}>
            Selecione uma turma
          </Typography>
          <IntegralProfessorTable listaIP={listaIP} setIntegralProfessor={handleSelectIP} />
        </Box>
      );
    }
    if (integralPresenca) {
      return <IntegralPresencaAlunoTable key={integralPresenca.codigo} integralPresenca={integralPresenca} />;
    }
    return (
      <Stack spacing={3}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={2}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                Turma
              </Typography>
              <Typography variant="subtitle1" fontWeight={600}>
                {integralProfessor.turma?.descricao}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Professor: {integralProfessor.usuario?.nome ?? "--"}
              </Typography>
            </Box>
            <Button variant="outlined" onClick={() => handleSelectIP(null)}>
              Trocar Turma
            </Button>
          </Stack>
        </Paper>

        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} mb={1.5}>
            <Typography variant="subtitle1" fontWeight={600}>
              Dias registrados
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditando({ integralProfessor, eNovo: true })}>
              Registrar Dia
            </Button>
          </Stack>
          {isLoadingP ? (
            <Box py={6}>
              <Loading description="Carregando dias registrados..." />
            </Box>
          ) : (
            <IntegralPresencaTable
              listaPresenca={listaPresenca}
              setIntegralPresenca={setIntegralPresenca}
              handleChangePage={(event, newPage) => setPageNumber(newPage)}
            />
          )}
        </Box>
      </Stack>
    );
  };

  return (
    <Container>
      <IntegralPresencaForm
        integralPresenca={editando}
        onClose={() => setEditando(null)}
        onSave={handleSave}
        isSaving={isSaving}
      />
      {renderHeader()}
      {renderContent()}
    </Container>
  );
};

export default IntegralPresencaContainer;
